import { useEffect, useMemo, useState } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { writeLog } from '../../utils/firebaseUtils';
import { submitTaskForReview } from '../../utils/taskClosure';

const S = {
  bg: 'var(--smc-bg)', panel: 'var(--smc-panel)', border: 'var(--smc-bd)',
  text: 'var(--smc-tx)', dim: 'var(--smc-tx3)', muted: 'var(--smc-tx2)', accent: 'var(--smc-ac)',
  green: 'var(--smc-gn)', red: 'var(--smc-rd)', amber: 'var(--smc-am)',
  mono: 'var(--smc-mono, monospace)', sans: "'Inter', system-ui, sans-serif",
};

const MAX_MB = 25;

/**
 * Submit-for-review popup. Nobody closes a task alone: the assignee sends
 * their update (note and/or files) to a reviewer — the task creator by
 * default — who approves it to close the task or returns it (TaskQuickView).
 * A note or attachment is required unless the task opted out with
 * requiresProof:false. firestore.rules enforces the same.
 */
export default function TaskCompleteModal({ task, userId, userName, onClose, onCompleted }) {
  const [note, setNote] = useState('');
  const [attachments, setAttachments] = useState([]); // [{name,url,size,path}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [members, setMembers] = useState([]);
  const [reviewerUid, setReviewerUid] = useState(
    task.createdByUid && task.createdByUid !== userId ? task.createdByUid : '');

  useEffect(() => {
    getDocs(collection(getFirestore(getApp()), 'authorized_users'))
      .then(snap => setMembers(snap.docs.map(d => ({ uid: d.id, ...d.data() }))
        .filter(m => m.active !== false && m.uid !== userId)
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''))))
      .catch(() => {});
  }, [userId]);

  const reviewer = useMemo(() => {
    const m = members.find(x => x.uid === reviewerUid);
    if (m) return { uid: m.uid, name: m.name || m.email || '' };
    if (reviewerUid && reviewerUid === task.createdByUid) return { uid: reviewerUid, name: task.createdBy || '' };
    return null;
  }, [members, reviewerUid, task.createdByUid, task.createdBy]);

  const proofRequired = task.requiresProof !== false;
  const hasProof = !!note.trim() || attachments.length > 0;
  const canSubmit = !!reviewer && (hasProof || !proofRequired) && !uploading;

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      const { getStorage, ref, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();
      for (const file of files) {
        if (file.size > MAX_MB * 1024 * 1024) throw new Error(`${file.name} is larger than ${MAX_MB} MB`);
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `tasks/completions/${task.id}/${Date.now()}_${safeName}`;
        const uploadTask = uploadBytesResumable(ref(storage, path), file);
        await new Promise((res, rej) => uploadTask.on('state_changed', null, rej, res));
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setAttachments(prev => [...prev, { name: file.name, url, size: file.size, path,
          type: file.type.startsWith('image/') ? 'image' : 'file' }]);
      }
    } catch (ex) {
      setError('Upload failed: ' + (ex.message || 'Unknown error'));
    }
    setUploading(false);
  }

  async function submit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await submitTaskForReview(task, { note, attachments, reviewer, user: { uid: userId, name: userName } });
      writeLog('task_submitted_for_review', {
        taskId: task.id, title: task.title, reviewerUid: reviewer.uid,
        hadAttachment: attachments.length > 0,
      }).catch(() => {});
      onCompleted?.();
      onClose?.();
    } catch (ex) {
      setError(ex.code === 'permission-denied'
        ? 'Not allowed — only an assignee, the creator or a manager can submit this task.'
        : (ex.message || 'Could not submit'));
      setSubmitting(false);
    }
  }

  const label = { fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: 1, marginBottom: 6, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div role="dialog" aria-modal="true" aria-label="Submit task for review"
        style={{ width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', background: S.panel,
          border: `1px solid ${S.border}`, borderRadius: 10, color: S.text }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${S.border}`,
          display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: S.mono, fontSize: 9, color: S.green, letterSpacing: 2 }}>SUBMIT FOR REVIEW</span>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', color: S.dim, cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontFamily: S.sans, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{task.title}</div>
          <p style={{ fontFamily: S.sans, fontSize: 12, color: S.muted, margin: '0 0 14px', lineHeight: 1.5 }}>
            A task closes only after a second person approves it. Add your update and documents, then pick who reviews it.
          </p>

          <label htmlFor="tcm-note" style={label}>UPDATE {proofRequired ? '(note or file required)' : '(optional)'}</label>
          <textarea id="tcm-note" value={note} onChange={e => setNote(e.target.value)}
            placeholder="What was done, results, links, anything the reviewer should check…"
            rows={4}
            style={{ width: '100%', boxSizing: 'border-box', background: S.bg, border: `1px solid ${S.border}`,
              color: S.text, fontFamily: S.sans, fontSize: 13, lineHeight: 1.6, padding: '10px 12px',
              resize: 'vertical', marginBottom: 10, borderRadius: 6 }} />

          {attachments.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {attachments.map((a, i) => (
                <div key={a.path || i} style={{ display: 'flex', alignItems: 'center', gap: 8,
                  fontFamily: S.mono, fontSize: 10, color: S.accent }}>
                  <span>📎 {a.name}</span>
                  <button onClick={() => setAttachments(p => p.filter((_, j) => j !== i))} aria-label={`Remove ${a.name}`}
                    style={{ background: 'none', border: 'none', color: S.red, cursor: 'pointer', fontSize: 12 }}>×</button>
                </div>
              ))}
            </div>
          )}
          <label style={{ display: 'inline-block', fontFamily: S.mono, fontSize: 9, padding: '6px 12px', cursor: 'pointer',
            border: `1px solid ${S.border}`, color: S.muted, borderRadius: 4, marginBottom: 14 }}>
            {uploading ? 'UPLOADING…' : '📎 ATTACH DOCUMENTS'}
            <input type="file" multiple hidden onChange={handleFiles} disabled={uploading} />
          </label>

          <label htmlFor="tcm-reviewer" style={label}>REVIEWER</label>
          <select id="tcm-reviewer" value={reviewerUid} onChange={e => setReviewerUid(e.target.value)}
            style={{ width: '100%', background: S.bg, border: `1px solid ${reviewer ? S.border : S.amber}`,
              color: S.text, fontFamily: S.sans, fontSize: 13, padding: '8px 10px', borderRadius: 6 }}>
            <option value="">Choose who approves this…</option>
            {task.createdByUid && task.createdByUid !== userId && !members.some(m => m.uid === task.createdByUid) && (
              <option value={task.createdByUid}>{task.createdBy || 'Task creator'} (created this task)</option>
            )}
            {members.map(m => (
              <option key={m.uid} value={m.uid}>
                {m.name || m.email}{m.uid === task.createdByUid ? ' (created this task)' : ''}
              </option>
            ))}
          </select>
          {task.createdByUid === userId && (
            <div style={{ fontFamily: S.sans, fontSize: 11, color: S.dim, marginTop: 6 }}>
              You created this task, so someone else has to approve it.
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <div style={{ flex: 1 }} />
            <button onClick={onClose}
              style={{ fontFamily: S.mono, fontSize: 9, padding: '8px 14px', cursor: 'pointer',
                background: 'transparent', border: `1px solid ${S.border}`, color: S.muted, borderRadius: 4 }}>
              CANCEL
            </button>
            <button onClick={submit} disabled={!canSubmit || submitting}
              style={{ fontFamily: S.mono, fontSize: 9, padding: '8px 16px', cursor: canSubmit ? 'pointer' : 'default',
                background: canSubmit ? S.green : 'transparent',
                border: `1px solid ${canSubmit ? S.green : S.border}`,
                color: canSubmit ? '#fff' : S.dim, fontWeight: 700, borderRadius: 4 }}>
              {submitting ? 'SENDING…' : 'SEND FOR APPROVAL'}
            </button>
          </div>
          {error && <div role="alert" style={{ fontFamily: S.mono, fontSize: 10, color: S.red, marginTop: 10 }}>{error}</div>}
        </div>
      </div>
    </div>
  );
}
